/**
 * Per-game result mappers (v2) — TypeScript mirror. Spec §3.4/§3.5.
 * Byte-identical to 155-game-crash/fairness/mappers.py; locked by fairness-vectors.json.
 */

import * as core from './core'

/**
 * Half-up 2dp rounding of a PAID multiplier: floor(x*100 + 0.5) / 100 via the
 * exact float op. NOT Math.round(x*100)/100 and NOT toFixed — the paid value
 * must match Python floor(x*100+0.5)/100 exactly (v1 used Math.round here, which
 * diverged from Python's banker's round() and was hidden by a ±0.005 tolerance).
 */
export function roundMultiplier(x: number): number {
  return Math.floor(x * 100 + 0.5) / 100
}

/** Crash point. max BEFORE rounding. Uncapped; instant-crash where r < edge. */
export function crashMultiplier(r: number, houseEdge = 0.06): number {
  return roundMultiplier(Math.max(1.0, (1.0 - houseEdge) / (1.0 - r)))
}

/**
 * Uniform integer in [0, n) via exact integer scaling: (int(hex13) * n) >> 52.
 * MUST use BigInt — int(hex13) can be up to 2^52, and *n overflows Number's
 * 2^53 exact-integer range, so a float multiply would mis-bucket at boundaries.
 */
export function scaledIndex(hex13: string, n: number): number {
  return Number((BigInt('0x' + hex13) * BigInt(n)) >> BigInt(52))
}

// ── vehicle / counting games (Rush Hour, Duck River, Snow Run, Social) ───────
//
// These are RESULT-FIRST: the clip's final count is already known when the round
// opens, the house picks WHICH outcome wins, and the boundaries are then built
// around the count to realise it. So everything the house chooses must derive
// from the committed seed — that is exactly what a player needs to prove the
// boundaries were not selected to exploit the operator's advance knowledge.

/**
 * Canonical outcome order. NEVER iterate the probabilities object — JS object
 * key order is not guaranteed the way Python's insertion order is, and the
 * cumulative selection below depends on a fixed order. Append-only, forever.
 */
export const OUTCOME_ORDER = ['under', 'range', 'over', 'jackpot'] as const

export type CountingOutcome = (typeof OUTCOME_ORDER)[number]

/** Weights travel as integer parts-per-million so no verifier repeats a float->int
 *  conversion (and a distribution that doesn't sum to 1 fails loudly). */
export const WEIGHT_SCALE = 1_000_000

export type CountingProbabilities = Partial<Record<CountingOutcome, number>>

export interface CountingBoundaries {
  outcome: CountingOutcome
  lowerBound: number
  upperBound: number
  gap: number
  outcomes: CountingOutcome[]
  weightsPpm: number[]
  /** label -> 13-hex draw, exactly as published in the round's reveal. */
  draws: Record<string, string>
}

/**
 * Canonically-ordered outcomes + integer ppm weights summing to WEIGHT_SCALE.
 *
 * The sum guard is the check the 2026 jackpot drift needed: a lone tweak (e.g.
 * dropping jackpot 0.05 -> 0.04) no longer silently re-prices a bet, it throws.
 */
export function weightsPpm(
  probabilities: CountingProbabilities
): { order: CountingOutcome[]; weights: number[] } {
  const order = OUTCOME_ORDER.filter((o) => o in probabilities)
  const unknown = Object.keys(probabilities).filter(
    (k) => !(OUTCOME_ORDER as readonly string[]).includes(k)
  )
  if (unknown.length > 0) {
    throw new Error(
      `unknown outcome(s) ${JSON.stringify(unknown.sort())}; expected ${JSON.stringify(OUTCOME_ORDER)}`
    )
  }
  const weights = order.map((o) => Math.floor((probabilities[o] as number) * WEIGHT_SCALE + 0.5))
  const total = weights.reduce((a, b) => a + b, 0)
  if (total !== WEIGHT_SCALE) {
    throw new Error(`probabilities must sum to 1.0 (${WEIGHT_SCALE} ppm), got ${total}`)
  }
  return { order: [...order], weights }
}

/**
 * Pick a weighted outcome from a 52-bit draw using INTEGER cumulative buckets.
 * No float touches the decision, so Python/JS/Go agree exactly. Bias vs a true
 * uniform is 1e6 / 2**52 ~= 2.2e-10, i.e. one mis-bucket per ~4.5e9 rounds.
 */
export function weightedPick(
  hex13: string,
  order: CountingOutcome[],
  wPpm: number[]
): CountingOutcome {
  // Index-alignment is the whole contract of this pair; a short/long weights
  // array would silently re-price the distribution, so refuse it outright.
  if (order.length === 0 || order.length !== wPpm.length) {
    throw new Error(
      `outcomes and weights must be non-empty and index-aligned (${order.length} vs ${wPpm.length})`
    )
  }
  const target = scaledIndex(
    hex13,
    wPpm.reduce((a, b) => a + b, 0)
  )
  let cum = 0
  for (let i = 0; i < order.length; i++) {
    cum += wPpm[i]!
    if (target < cum) return order[i]!
  }
  return order[order.length - 1]! // unreachable while the weights sum to the scale
}

/**
 * Seed -> (outcome, lowerBound, upperBound) for a counting round.
 *
 * Every branch uses its OWN domain label at index 0, so adding a future draw can
 * never shift an existing one. `gap` is CONSTANT across outcomes on purpose: a
 * varying gap leaked which outcome was favoured (worth ~120% RTP before it was
 * fixed), so the width must carry no information.
 */
export async function vehicleBoundaries(
  serverSeed: string,
  probabilities: CountingProbabilities,
  finalCount: number,
  { beacon = '', gap = 3 }: { beacon?: string; gap?: number } = {}
): Promise<CountingBoundaries> {
  const { order, weights } = weightsPpm(probabilities)
  const hasJackpot = 'jackpot' in probabilities
  const draws: Record<string, string> = {}

  const draw = async (label: string): Promise<string> => {
    const h = await core.draw13(serverSeed, beacon, label, 0)
    draws[label] = h
    return h
  }

  const outcome = weightedPick(await draw('outcome'), order, weights)

  let lowerBound: number
  let upperBound: number

  if (outcome === 'under') {
    lowerBound = finalCount + 1 + scaledIndex(await draw('under_offset'), 2)
    upperBound = lowerBound + gap
  } else if (outcome === 'over') {
    upperBound = finalCount - (1 + scaledIndex(await draw('over_offset'), 2))
    lowerBound = upperBound - gap
  } else if (outcome === 'range') {
    const h = await draw('range_offset')
    // 4-way range is STRICTLY between the bounds; 3-way is INCLUSIVE of them.
    const offset = hasJackpot ? 1 + scaledIndex(h, gap - 1) : scaledIndex(h, gap + 1)
    lowerBound = finalCount - offset
    upperBound = lowerBound + gap
  } else {
    // jackpot — the count is pinned to a boundary (drives the Exact side bets)
    if (scaledIndex(await draw('jackpot_side'), 2) === 0) {
      lowerBound = finalCount
      upperBound = finalCount + gap
    } else {
      lowerBound = finalCount - gap
      upperBound = finalCount
    }
  }

  // Defensive invariants, identical to the engine's generator. They must live
  // HERE (not in the caller) because a verifier has to reproduce the PUBLISHED
  // bounds, clamps included.
  lowerBound = Math.max(1, lowerBound)
  upperBound = Math.max(lowerBound + gap, upperBound)

  return { outcome, lowerBound, upperBound, gap, outcomes: order, weightsPpm: weights, draws }
}

// ── marble races (Plinko, Snake, Rolling Dunes, Coin Flip, Fast Lane, Stairpong,
//    Survivor, Fish Tank) — the "marble_order" family ─────────────────────────
//
// A round's outcome is an ORDERED finishing list of K outcome-names taken from M
// entrants (K == 1 for the single-winner games; K == 3 for the podium games).
// Unlike the counting family (whose final_count is a measured input), the
// finishing ORDER is fully seed-derived: one domain-separated draw at label
// "order" decides the whole order, exactly reproducing the engine's single
// CSPRNG choice over the clip-covered result combos. A verifier re-enumerates
// the permutations from (marbles, K) itself — no combo list is published.

export interface MarbleOrderResult {
  /** The finishing order, 1st..Kth, by entrant name. */
  order: string[]
  /** The entrant universe for THIS round, in canonical order. */
  marbles: string[]
  /** Podium size (1 for single-winner games). */
  k: number
  /** The single 13-hex draw at label "order", index 0. */
  draw: string
  /** For equal games: the chosen k-permutation index. For weighted: the winner
   *  index into `marbles`. */
  index: number
  /** Present only for WEIGHTED single-winner games; omitted for equal games. */
  weightsPpm?: number[]
}

/**
 * All k-permutations of range(m) in CANONICAL (lexicographic) order.
 *
 * This ordering is THE contract: the engine picks an index into this list and
 * the verifier must enumerate it identically. Built explicitly (NOT a library
 * permutations call) so JS/Go ports reproduce the exact same order. The count is
 * m!/(m-k)! — up to 336 for the 8-entrant podium games — which is why the index
 * scaling below must go through BigInt (see scaledIndex).
 */
export function permutationsIndex(m: number, k: number): number[][] {
  if (!(k > 0 && k <= m)) throw new Error(`need 0 < k <= m, got m=${m} k=${k}`)
  let result: number[][] = [[]]
  for (let step = 0; step < k; step++) {
    const nxt: number[][] = []
    for (const prefix of result) {
      for (let i = 0; i < m; i++) {
        if (!prefix.includes(i)) nxt.push([...prefix, i])
      }
    }
    result = nxt
  }
  return result
}

/**
 * Seed -> ordered finishing list for a marble round. Replaces the engine's
 * secrets.SystemRandom choice one-for-one:
 *   equal    -> uniform index into the k-permutations of the entrant list
 *   weighted -> weighted_pick over the entrants (K==1 only, the live weighted mode)
 *
 * A SINGLE domain-separated draw at label "order", index 0 decides the whole
 * order — matching the engine's single choice, not a per-position Fisher-Yates.
 * Byte-identical to fairness/mappers.py::marble_order; locked by
 * fairness-vectors.json.
 */
export async function marbleOrder(
  serverSeed: string,
  marbles: string[],
  k: number,
  { beacon = '', weightsPpm = null }: { beacon?: string; weightsPpm?: number[] | null } = {}
): Promise<MarbleOrderResult> {
  const m = marbles.length
  if (m === 0) throw new Error('marbles must be non-empty')
  if (!(k > 0 && k <= m)) throw new Error(`need 0 < k <= m, got m=${m} k=${k}`)

  const draw = await core.draw13(serverSeed, beacon, 'order', 0)

  if (weightsPpm !== null) {
    // Weighted single-winner (Stairpong / Survivor / Fish Tank). The engine's
    // weighted mode is K==1 in every live game; a weighted podium would need a
    // without-replacement variant no game exercises, so refuse it loudly.
    if (k !== 1) throw new Error(`weighted marble_order supports only k=1, got k=${k}`)
    if (weightsPpm.length !== m) {
      throw new Error(`weights_ppm must be index-aligned to marbles (${weightsPpm.length} vs ${m})`)
    }
    const total = weightsPpm.reduce((a, b) => a + b, 0)
    if (total !== WEIGHT_SCALE) {
      throw new Error(`weights_ppm must sum to ${WEIGHT_SCALE}, got ${total}`)
    }
    const index = scaledIndex(draw, WEIGHT_SCALE)
    let cum = 0
    let picked = m - 1
    for (let i = 0; i < weightsPpm.length; i++) {
      cum += weightsPpm[i]!
      if (index < cum) {
        picked = i
        break
      }
    }
    return {
      order: [marbles[picked]!],
      marbles: [...marbles],
      k,
      draw,
      index: picked,
      weightsPpm: [...weightsPpm]
    }
  }

  // Equal: uniform pick over the canonical k-permutations of the entrants.
  const perms = permutationsIndex(m, k)
  const index = scaledIndex(draw, perms.length)
  const order = perms[index]!.map((i) => marbles[i]!)
  return { order, marbles: [...marbles], k, draw, index }
}
